import React, { FunctionComponent, useMemo } from "react";
import { fade, Grid, GridProps, lighten, makeStyles, Theme, Typography, useTheme } from "@material-ui/core";
import { MenuCategoryDictionary } from "../../models/categories";
import { CategoryItemMenuItem } from "./category-item-menu-item";
import { getCategoryItemHeight } from "./category-item";
import { useTranslation } from "react-i18next";
import { MenuItemCategory } from "../../models/types/category-types";
import AnimateHeight from "react-animate-height";

export const CATEGORY_MENU_WIDTH = 228;
export const CATEGORY_MENU_MIN_HEIGHT = 180;
export const CATEGORY_MENU_FONT_SIZE = 16;
export const CATEGORY_MENU_PADDING_SPACING_X = 3;
export const CATEGORY_MENU_PADDING_SPACING_Y = 1;
export const CATEGORY_MENU_OFFSET = 15;
export const CATEGORY_MENU_PADDING_BOTTOM = 2;

interface ICategoryItemMenuStyleProps {
  open: boolean;
  openUpwards: boolean;
  minHeight: number;
  color: string;
  headerBackgroundColor: string;
  menuItemsBackgroundColor: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  menu: {
    overflow: "hidden",
    borderRadius: 20,
    transition: "all 0.3s ease-in ",
  },
  title: {
    fontSize: CATEGORY_MENU_FONT_SIZE,
    fontWeight: 700,
    color: "#222222",
    padding: `${theme.spacing(4)}px 0`,
  },
  menuWrapper: {
    position: "absolute",
    // paddingLeft: theme.spacing(3),
    right: -CATEGORY_MENU_WIDTH - CATEGORY_MENU_OFFSET,
    width: CATEGORY_MENU_WIDTH,
    visibility: ({ open }: ICategoryItemMenuStyleProps) => (open ? "visible" : "hidden"),
    top: ({ openUpwards }: ICategoryItemMenuStyleProps) => (!openUpwards ? 0 : undefined),
    bottom: ({ openUpwards }: ICategoryItemMenuStyleProps) => (openUpwards ? 0 : undefined),
    opacity: ({ open }: ICategoryItemMenuStyleProps) => (open ? 1 : 0),
    transform: ({ open }: ICategoryItemMenuStyleProps) => `translate3d(${open ? -2 : -20}px,0,0)`,
    boxShadow: ({ open, color }: ICategoryItemMenuStyleProps) =>
      open ? `0 0px 20px 0 ${lighten(color, 0.2)}` : "0 0px 0px 0 rgba(188, 220, 151, 0)",
    borderRadius: 20,
    transition: "all 0.3s ease-in ",
    transitionProperty: "box-shadow,  min-height, transform, opacity, visibility",
  },
  headerContainer: {
    backgroundColor: ({ headerBackgroundColor }: ICategoryItemMenuStyleProps) => headerBackgroundColor,
  },
  menuItemsContainer: {
    paddingTop: theme.spacing(1),
    backgroundColor: ({ menuItemsBackgroundColor }: ICategoryItemMenuStyleProps) => menuItemsBackgroundColor,
    paddingBottom: theme.spacing(2),
  },
}));

interface ICategoryItemMenuProps extends GridProps {
  open: boolean;
  category: MenuItemCategory;
}

export const CategoryItemMenu: FunctionComponent<ICategoryItemMenuProps> = (props) => {
  const { open, onMouseEnter, onMouseLeave, category, ...rest } = props;
  const { color, subcategories } = MenuCategoryDictionary[category];
  const theme = useTheme();
  const { t } = useTranslation();
  const openUpwards = useMemo(() => category === MenuItemCategory.DIET, [category]);
  const minHeight = useMemo(() => getCategoryItemHeight(theme), [theme]);
  const classes = useStyles({
    open,
    openUpwards,
    minHeight,
    color: color ?? theme.palette.primary.main,
    headerBackgroundColor: fade(lighten(color!, 0.4), 0.87),
    menuItemsBackgroundColor: fade(color!, 1),
  });

  return (
    <Grid onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} className={classes.menuWrapper}>
      <AnimateHeight
        id={`animate-height-${MenuCategoryDictionary[category].color}`}
        duration={400}
        height={open ? "auto" : 0}
      >
        <Grid container direction="column" alignItems="center" className={classes.menu} {...rest}>
          <Grid item container justify="center" xs={12} className={classes.headerContainer}>
            <Typography key={`title-${category}`} className={classes.title}>
              {t(`menu_item_category.${category}`)}
            </Typography>
          </Grid>
          <Grid item container direction="column" xs={12} className={classes.menuItemsContainer}>
            {subcategories.map((subcategory) => (
              <CategoryItemMenuItem key={subcategory} subcategory={subcategory} category={category} />
            ))}
          </Grid>
        </Grid>
      </AnimateHeight>
    </Grid>
  );
};
